import { useState, useEffect, useMemo } from 'react';
import {
  BROWSE_CATEGORY_OPTIONS,
  BROWSE_DEFAULT_PRICE_MIN,
  BROWSE_DEFAULT_PRICE_MAX,
  COUNTRY_FILTER_FEATURED,
} from '../config/constants';
import {
  getRegionalMarketLabel,
  getRegionLabel,
  inferCountryFromSearchQuery,
  propertyMatchesBrowseMarket,
  isMultiMarketView,
} from '../data/countries';
import {
  filterPropertiesByListingMode,
  getPropertyListingMode,
  dedupePropertiesById,
  isPropertyFavorited,
  isPropertyComparing,
  propertyMatchesBrowseCategory,
  propertyMatchesBrowsePriceRange,
  propertyMatchesBrowseSearch,
  propertyVisibleOnSite,
  propertyMatchesListedDateFilter,
  isPropertyOpenForPrimaryInvestment,
} from '../utils/properties';
import BrowseListedDateFilter from '../components/listings/BrowseListedDateFilter';
import useListPagination from '../hooks/useListPagination';
import CountrySelector from '../components/shared/CountrySelector';
import PropertyCard from '../components/property/PropertyCard';
import ListingsViewToggle from '../components/listings/ListingsViewToggle';
import ListingsMapBrowse from '../components/listings/ListingsMapBrowse';
import PaginationBar from '../components/shared/PaginationBar';
import PageRefinedHero from '../components/layout/PageRefinedHero';

const ViewListingsPage = ({
  properties,
  catalogProperties,
  favorites,
  compareList,
  onToggleFav,
  onToggleCompare,
  onSelectProperty,
  onContactAgent,
  country,
  onCountryChange,
  loading,
  browsePreset = "default",
}) => {
  const [search, setSearch] = useState(() => {
    try { return sessionStorage.getItem("coown_browse_search") || ""; } catch { return ""; }
  });
  const [listingMode, setListingMode] = useState(() => {
    try {
      const m = sessionStorage.getItem("coown_browse_listing_mode");
      if (m === "sale" || m === "all") return m;
    } catch (_) {}
    return "all";
  });
  const [rentScopeActive, setRentScopeActive] = useState(browsePreset === "rent");
  const [category, setCategory] = useState("Any Category");
  const [priceMin, setPriceMin] = useState(BROWSE_DEFAULT_PRICE_MIN);
  const [priceMax, setPriceMax] = useState(BROWSE_DEFAULT_PRICE_MAX);
  const [listingsView, setListingsView] = useState("grid");
  const [listedDateMode, setListedDateMode] = useState("all");
  const [listedYear, setListedYear] = useState("");
  const [listedMonth, setListedMonth] = useState("");
  const [listedDate, setListedDate] = useState("");

  useEffect(() => {
    let handoffMode = null;
    let handoffSearch = null;
    try {
      handoffMode = sessionStorage.getItem("coown_browse_listing_mode");
      handoffSearch = sessionStorage.getItem("coown_browse_search");
      sessionStorage.removeItem("coown_browse_search");
      sessionStorage.removeItem("coown_browse_listing_mode");
      sessionStorage.removeItem("coown_browse_rent_results_only");
    } catch (_) {}

    const directVisit = handoffMode == null && (handoffSearch == null || handoffSearch === "");
    if (directVisit) {
      onCountryChange(COUNTRY_FILTER_FEATURED);
      setListingMode("all");
      setRentScopeActive(browsePreset === "rent");
      setSearch("");
      setPriceMin(BROWSE_DEFAULT_PRICE_MIN);
      setPriceMax(BROWSE_DEFAULT_PRICE_MAX);
    } else if (handoffSearch) {
      setSearch(handoffSearch);
      const inferred = inferCountryFromSearchQuery(handoffSearch);
      if (inferred) onCountryChange(inferred);
    }
  }, [browsePreset]);

  useEffect(() => {
    const inferred = inferCountryFromSearchQuery(search);
    if (inferred && inferred !== country) onCountryChange(inferred);
  }, [search]);

  const source = dedupePropertiesById(catalogProperties?.length ? catalogProperties : properties);
  const filtered = useMemo(() => {
    let list = Array.isArray(source) ? [...source] : [];
    if (listingMode === "sale") list = filterPropertiesByListingMode(list, "sale");
    else if (listingMode === "rent") list = filterPropertiesByListingMode(list, "rent");
    else if (rentScopeActive) list = filterPropertiesByListingMode(list, "rent");
    return list.filter((p) =>
      propertyVisibleOnSite(p) &&
      (getPropertyListingMode(p) === 'rent' || isPropertyOpenForPrimaryInvestment(p)) &&
      propertyMatchesBrowseMarket(p, country, search) &&
      propertyMatchesBrowseSearch(p, search) &&
      propertyMatchesBrowseCategory(p, category) &&
      propertyMatchesBrowsePriceRange(p, priceMin, priceMax) &&
      propertyMatchesListedDateFilter(p, {
        mode: listedDateMode,
        year: listedYear,
        month: listedMonth,
        date: listedDate,
      })
    );
  }, [
    source,
    listingMode,
    rentScopeActive,
    country,
    search,
    category,
    priceMin,
    priceMax,
    listedDateMode,
    listedYear,
    listedMonth,
    listedDate,
  ]);

  const browsePaginationKey = [
    listingMode,
    rentScopeActive,
    country,
    search,
    category,
    priceMin,
    priceMax,
    listedDateMode,
    listedYear,
    listedMonth,
    listedDate,
  ].join("|");
  const {
    items: browsePageItems,
    page: browsePage,
    total: browseTotal,
    totalPages: browseTotalPages,
    startIndex: browseStart,
    endIndex: browseEnd,
    hasPrev: browseHasPrev,
    hasNext: browseHasNext,
    goToPage: browseGoToPage
  } = useListPagination(filtered, browsePaginationKey);

  const priceFieldLabel = listingMode === "rent"
    ? "Monthly rent range (USD)"
    : listingMode === "sale"
      ? "Sale price range (USD)"
      : "Price range (USD)";

  const emptyState = (
    <div style={{ textAlign: "center", padding: "3rem 1rem", background: "var(--card)", borderRadius: "var(--radius)", border: "1px solid var(--border)" }}>
      <i className="fas fa-search" style={{ fontSize: "2.5rem", color: "var(--text-light)", marginBottom: "1rem" }}></i>
      <h3 style={{ marginBottom: "0.5rem" }}>No listings match</h3>
      <p style={{ color: "var(--text-light)", maxWidth: "480px", margin: "0 auto" }}>
        Try another market, listing mode, search term, price range, or listed date.
      </p>
    </div>
  );

  const loadingSpinner = (
    <div style={{ textAlign: "center", padding: "2rem" }}>
      <i className="fas fa-spinner fa-spin" style={{ fontSize: "2rem", color: "var(--primary)" }}></i>
    </div>
  );

  return (
    <div className={`coown-page coown-page--hero browse-hub-page${browsePreset === 'rent' ? ' browse-hub-page--rent' : ''}`}>
      {browsePreset === 'rent' && (
        <PageRefinedHero
          badge="Rent with CoOwn"
          title="Find your"
          titleAccent="rental"
          subtitle="Browse verified rental listings across top markets. Filter by location, price, and availability — then connect with listing agents."
          benefits={[
            { icon: 'fas fa-search-location', title: 'Verified listings', subtitle: 'Every rental reviewed before publish' },
            { icon: 'fas fa-sliders-h', title: 'Smart filters', subtitle: 'Search by market, price, and date' },
            { icon: 'fas fa-comments', title: 'Direct contact', subtitle: 'Message agents from each listing' },
          ]}
        />
      )}
      <div className="browse-hub-inner">
        <div className="browse-hub-panel">
          <div className="browse-field">
            <label className="browse-field-label" htmlFor="browse-search-query">Search query</label>
            <div className="browse-search-combo">
              <div className="browse-search-combo-input-wrap">
                <i className="fas fa-search" aria-hidden="true"></i>
                <input
                  id="browse-search-query"
                  type="search"
                  placeholder="Country, city, state, zip, property name..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  aria-label="Search listings"
                />
              </div>
              <div className="browse-search-combo-market">
                <CountrySelector selected={country} onChange={onCountryChange} variant="searchCombo" />
              </div>
            </div>
          </div>

          <div className="browse-field">
            <span className="browse-field-label">Listing mode</span>
            <div className="browse-mode-segment" role="group" aria-label="Listing mode">
              {[
                { id: "all", label: "ALL" },
                { id: "sale", label: "SALE" },
                { id: "rent", label: "RENT" },
              ].map(({ id, label }) => (
                <button
                  key={id}
                  type="button"
                  className={listingMode === id ? "active" : ""}
                  onClick={() => {
                    if (id === "all" && rentScopeActive) {
                      setRentScopeActive(false);
                      setListingMode("all");
                    } else {
                      setRentScopeActive(false);
                      setListingMode(id);
                    }
                  }}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          <BrowseListedDateFilter
            dateMode={listedDateMode}
            onDateModeChange={setListedDateMode}
            listedYear={listedYear}
            onListedYearChange={setListedYear}
            listedMonth={listedMonth}
            onListedMonthChange={setListedMonth}
            listedDate={listedDate}
            onListedDateChange={setListedDate}
            onClear={() => {
              setListedYear("");
              setListedMonth("");
              setListedDate("");
            }}
          />

          <hr className="browse-hub-divider" />

          <div className="browse-field-row">
            <div className="browse-field">
              <label className="browse-field-label" htmlFor="browse-category">Property category</label>
              <select
                id="browse-category"
                className="browse-native-select"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
              >
                {BROWSE_CATEGORY_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
            </div>
            <div className="browse-field">
              <label className="browse-field-label">{priceFieldLabel}</label>
              <div className="browse-price-range">
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={priceMin}
                  onChange={(e) => setPriceMin(e.target.value)}
                  aria-label="Minimum price"
                  placeholder="Min"
                />
                <span>to</span>
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={priceMax}
                  onChange={(e) => setPriceMax(e.target.value)}
                  aria-label="Maximum price"
                  placeholder="Max"
                />
              </div>
              <p className="browse-price-hint">
                Leave max empty for no upper limit (default: $1 – max).
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="browse-results-wrap" data-listings-anchor>
        <div className="browse-results-header">
          <h2>{browsePreset === "rent" && rentScopeActive ? "Rental listings" : getRegionalMarketLabel(country)}</h2>
          <div className="listings-meta">
            <span className="listings-count browse-results-count">
              {filtered.length} {filtered.length === 1 ? "listing" : "listings"}
            </span>
            <ListingsViewToggle view={listingsView} onChange={setListingsView} />
          </div>
        </div>
        {loading && loadingSpinner}
        {!loading && filtered.length === 0 && emptyState}
        {!loading && filtered.length > 0 && (
          <>
            {listingsView === "map" ? (
              <ListingsMapBrowse
                properties={browsePageItems}
                onSelectProperty={onSelectProperty}
                onContactAgent={onContactAgent}
                country={country}
              />
            ) : (
            <div className="properties-grid">
              {browsePageItems.map((p) => (
                <PropertyCard
                  key={p.id}
                  p={p}
                  isFavorite={isPropertyFavorited(favorites, p.id)}
                  isComparing={isPropertyComparing(compareList, p.id)}
                  onToggleFav={onToggleFav}
                  onToggleCompare={onToggleCompare}
                  onClick={() => onSelectProperty(p)}
                  country={country}
                />
              ))}
            </div>
            )}
            <PaginationBar
              page={browsePage}
              totalPages={browseTotalPages}
              total={browseTotal}
              startIndex={browseStart}
              endIndex={browseEnd}
              hasPrev={browseHasPrev}
              hasNext={browseHasNext}
              onPageChange={browseGoToPage}
              itemLabel="listings"
            />
          </>
        )}
      </div>
    </div>
  );
};

export default ViewListingsPage;
